// src/pages/settings/sms/SmsConfigPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, Settings, Zap, Send, History } from 'lucide-react';
import {
  getSmsGateways,
  getBusinessSmsConfig,
  createBusinessSmsConfig,
  updateBusinessSmsConfig,
  deleteBusinessSmsConfig,
} from '../../../services/api/smsConfig';
import { useAlert } from '../../../contexts/AlertContext';
import PageHeader from '../../../components/common/PageHeader/PageHeader';
import GatewaySetupPanel from './components/GatewaySetupPanel';
import TriggersPanel from './components/TriggersPanel';
import TestSmsPanel from './components/TestSmsPanel';
import SmsLogsPanel from './components/SmsLogsPanel';
import styles from './SmsConfigPage.module.css';

const TABS = [
  { id: 'config',   label: 'SMS Configuration', icon: Settings, requiresActive: false },
  { id: 'triggers', label: 'SMS Triggers',       icon: Zap,     requiresActive: true  },
  { id: 'test',     label: 'Test SMS',            icon: Send,    requiresActive: true  },
  { id: 'logs',     label: 'Log History',         icon: History, requiresActive: true  },
];

const SmsConfigPage = () => {
  const { success, error: showError } = useAlert();

  const [activeTab, setActiveTab] = useState('config');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [gateways, setGateways] = useState([]);
  const [config, setConfig] = useState(null);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      const [gwRes, cfgRes] = await Promise.all([
        getSmsGateways(),
        getBusinessSmsConfig(),
      ]);
      setGateways(gwRes.gateways || []);
      setConfig(cfgRes.config);
    } catch (err) {
      showError('Failed to load SMS configuration');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSelectGateway = async (smsGatewayId) => {
    try {
      setIsSaving(true);
      const res = await createBusinessSmsConfig({ smsGatewayId });
      setConfig(res.config);
      success('SMS gateway added. Fill in your credentials and save.');
    } catch (err) {
      showError(err.message || 'Failed to add gateway');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateConfig = async (updateData) => {
    try {
      setIsSaving(true);
      const res = await updateBusinessSmsConfig(updateData);
      setConfig(res.config);
      success('SMS configuration saved');
    } catch (err) {
      showError(err.message || 'Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfig = async () => {
    try {
      setIsSaving(true);
      await deleteBusinessSmsConfig();
      setConfig(null);
      setActiveTab('config');
      success('SMS configuration removed');
    } catch (err) {
      showError(err.message || 'Failed to remove configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const hasActiveConfig = config?.isActive;

  if (isLoading) {
    return (
      <div className={styles.page}>
        <PageHeader title="SMS Configuration" description="Configure SMS gateway and notification triggers" icon={MessageSquare} />
        <div className={styles.loadingState}>
          <div className={styles.spinner} />
          <p>Loading SMS configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="SMS Configuration"
        description="Configure SMS gateway and notification triggers"
        icon={MessageSquare}
      />

      <div className={styles.tabBar}>
        {TABS.map(tab => {
          const Icon = tab.icon;
          const disabled = tab.requiresActive && !hasActiveConfig;
          return (
            <button
              key={tab.id}
              className={`${styles.tabBtn} ${activeTab === tab.id ? styles.tabActive : ''} ${disabled ? styles.tabDisabled : ''}`}
              onClick={() => !disabled && setActiveTab(tab.id)}
              disabled={disabled}
              title={disabled ? 'Activate your SMS config first to use this tab' : undefined}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className={styles.tabContent}>
        {activeTab === 'config' && (
          <GatewaySetupPanel
            gateways={gateways}
            config={config}
            isSaving={isSaving}
            onSelectGateway={handleSelectGateway}
            onUpdateConfig={handleUpdateConfig}
            onDeleteConfig={handleDeleteConfig}
          />
        )}
        {activeTab === 'triggers' && hasActiveConfig && (
          <TriggersPanel config={config} />
        )}
        {activeTab === 'test' && hasActiveConfig && (
          <TestSmsPanel config={config} />
        )}
        {activeTab === 'logs' && hasActiveConfig && (
          <SmsLogsPanel />
        )}
      </div>
    </div>
  );
};

export default SmsConfigPage;